/**
 * Règles tarifaires officielles — Show Événement
 * ──────────────────────────────────────────────
 * • Tarifs publics identiques toute l'année
 * • Réduction Lun-Jeu : -15%
 * • Multi-jours : 3ème jour et + : -15%
 * • Options : livraison veille +60€ | récupération lendemain +60€
 * • Demi-journée : -25% en semaine (Lun→Ven), plein tarif le week-end (sans cumul -15%)
 */

// ─── Types de réservation ─────────────────────────────────────────────────────
export const BOOKING_TYPES = [
  {
    key: 'demi_matin',
    label: 'Demi-journée matin',
    sub: 'jusqu\'à 12h',
    mult: 0.75, // -25% en semaine ; week-end = plein tarif (calculé selon la date)
    delivery: 'Livraison entre 7h et 9h',
    icon: '🌅',
  },
  {
    key: 'demi_aprem',
    label: 'Demi-journée après-midi',
    sub: 'à partir de 13h',
    mult: 0.75, // -25% en semaine ; week-end = plein tarif (calculé selon la date)
    delivery: 'Livraison entre 13h et 14h',
    icon: '☀️',
  },
  {
    key: '1j',
    label: 'Journée complète',
    sub: '1 jour',
    mult: 1.00,
    delivery: 'Livraison entre 7h et 10h',
    icon: '📅',
  },
  {
    key: '2j',
    label: '2 jours',
    sub: '2 journées',
    mult: 2.00,
    delivery: 'Livraison entre 7h et 10h',
    icon: '📅📅',
  },
  {
    key: '3j',
    label: '3 jours',
    sub: '-15% sur le 3ème',
    mult: null, // calculé dynamiquement
    delivery: 'Livraison entre 7h et 10h',
    icon: '🗓️',
  },
  {
    key: '4j',
    label: '4 jours',
    sub: '-15% sur le 3ème et +',
    mult: null,
    delivery: 'Livraison entre 7h et 10h',
    icon: '🗓️',
  },
  {
    key: '5j',
    label: '5 jours',
    sub: '-15% sur le 3ème et +',
    mult: null,
    delivery: 'Livraison entre 7h et 10h',
    icon: '🗓️',
  },
  {
    key: '7j',
    label: '1 semaine',
    sub: '-15% sur le 3ème jour et +',
    mult: null,
    delivery: 'Livraison entre 7h et 10h',
    icon: '📆',
  },
];

// ─── Options supplémentaires ──────────────────────────────────────────────────
export const BOOKING_OPTIONS = {
  livraison_veille:    { label: 'Livraison la veille',          prix: 60, description: 'Installation possible dès la veille de l\'événement' },
  recuperation_surlen: { label: 'Récupération lendemain matin', prix: 60, description: 'Récupération le matin après votre événement (+60€)' },
};

// ─── Calcul du prix selon le nombre de jours ─────────────────────────────────
/**
 * Règle multi-jours :
 *   - Demi-journée : 50% du tarif 1j
 *   - 1 jour : tarif normal
 *   - 2 jours : 2 × tarif 1j
 *   - 3 jours et + : les 2 premiers jours × tarif 1j + les jours suivants × (tarif 1j × 0.85)
 */
export function isWeekendDay(dateStr) {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr);
    const day = d.getDay(); // 0=Dim, 6=Sam
    return day === 0 || day === 6;
  } catch { return false; }
}

export function calculateMultiDayPrice(pricePerDay, nbDays, bookingType = '1j', startDate = null) {
  if (!pricePerDay || pricePerDay <= 0) return 0;

  // Demi-journée : -25% en semaine (Lun→Ven) ⇒ 75% ; plein tarif le week-end (Sam/Dim)
  if (bookingType === 'demi_matin' || bookingType === 'demi_aprem' || bookingType === 'demi_journee') {
    const mult = isWeekendDay(startDate) ? 1.00 : 0.75;
    return Math.round(pricePerDay * mult * 100) / 100;
  }

  if (nbDays <= 0) return 0;
  if (nbDays === 1) return Math.round(pricePerDay * 100) / 100;
  if (nbDays === 2) return Math.round(pricePerDay * 2 * 100) / 100;

  // 3 jours et + : les 2 premiers à tarif plein, le reste à -15%
  const fullPriceDays    = 2;
  const discountedDays   = nbDays - fullPriceDays;
  const baseTotal        = pricePerDay * fullPriceDays;
  const discountedTotal  = pricePerDay * discountedDays * 0.85;
  return Math.round((baseTotal + discountedTotal) * 100) / 100;
}

/**
 * Réduction semaine (Lun-Jeu) : -15%
 * Retourne le prix après réduction si applicable, sinon le même prix
 */
export function applyWeekdayDiscount(price, dateStr) {
  if (!dateStr) return { price, hasDiscount: false, discountPct: 0 };
  try {
    const d = new Date(dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr);
    const day = d.getDay(); // 0=Dim, 1=Lun, 2=Mar, 3=Mer, 4=Jeu, 5=Ven, 6=Sam
    const isWeekday = day >= 1 && day <= 4; // Lundi à Jeudi
    if (isWeekday) {
      return {
        price:       Math.round(price * 0.85 * 100) / 100,
        hasDiscount: true,
        discountPct: 15,
        dayLabel:    ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][day],
      };
    }
  } catch { /* eslint-disable-line no-empty */ }
  return { price, hasDiscount: false, discountPct: 0 };
}

/**
 * Calcul complet avec toutes les règles appliquées
 */
export function calculateFullPrice({
  pricePerDay,
  nbDays       = 1,
  bookingType  = '1j',
  startDate    = null,
  options      = [],    // ['livraison_veille', 'recuperation_surlen']
  qty          = 1,
}) {
  if (!pricePerDay || pricePerDay <= 0) return getEmptyBreakdown();

  // 1. Prix de base multi-jours (demi-journée : -25% en semaine, plein tarif le week-end)
  const basePrice = calculateMultiDayPrice(pricePerDay, nbDays, bookingType, startDate);

  // 2. Réduction semaine Lun-Jeu (sur le prix de base) — PAS de cumul pour la demi-journée
  const isDemi = bookingType === 'demi_matin' || bookingType === 'demi_aprem' || bookingType === 'demi_journee';
  const weekday = isDemi
    ? { price: basePrice, hasDiscount: false, discountPct: 0 }
    : applyWeekdayDiscount(basePrice, startDate);
  const priceAfterWeekday = weekday.hasDiscount ? weekday.price : basePrice;

  // 3. Options
  let optionsTotal = 0;
  const optionsDetails = [];
  for (const optKey of options) {
    const opt = BOOKING_OPTIONS[optKey];
    if (opt) {
      optionsTotal += opt.prix;
      optionsDetails.push({ key: optKey, label: opt.label, prix: opt.prix });
    }
  }

  const totalHT    = priceAfterWeekday * qty + optionsTotal;
  const acompte    = Math.round(totalHT * 0.30 * 100) / 100;
  const solde      = Math.round(totalHT * 0.70 * 100) / 100;

  // Détail des réductions appliquées
  const reductions = [];
  if (nbDays >= 3 && bookingType !== 'demi_matin' && bookingType !== 'demi_aprem') {
    const saving3j = Math.round(pricePerDay * (nbDays - 2) * 0.15 * 100) / 100;
    reductions.push({
      label:   `Réduction multi-jours (${nbDays - 2}j × -15%)`,
      saving:  saving3j,
      pct:     15,
      color:   '#1F9D7A',
    });
  }
  if (weekday.hasDiscount) {
    reductions.push({
      label:   `Réduction semaine (${weekday.dayLabel})`,
      saving:  Math.round((basePrice - priceAfterWeekday) * qty * 100) / 100,
      pct:     15,
      color:   '#5DBCBF',
    });
  }

  return {
    pricePerDay,
    nbDays,
    bookingType,
    basePrice,
    weekdayDiscount:   weekday.hasDiscount ? weekday.discountPct : 0,
    priceAfterDiscount: priceAfterWeekday,
    optionsDetails,
    optionsTotal,
    qty,
    subtotal:    priceAfterWeekday * qty,
    total:       totalHT,
    acompte,
    solde,
    reductions,
    hasReductions: reductions.length > 0,
  };
}

function getEmptyBreakdown() {
  return {
    pricePerDay: 0, nbDays: 1, bookingType: '1j',
    basePrice: 0, weekdayDiscount: 0, priceAfterDiscount: 0,
    optionsDetails: [], optionsTotal: 0, qty: 1,
    subtotal: 0, total: 0, acompte: 0, solde: 0,
    reductions: [], hasReductions: false,
  };
}

// ─── Mapping nb jours → bookingType ───────────────────────────────────────────
export function nbJoursToBookingType(nb) {
  if (nb <= 0) return '1j';
  if (nb === 1) return '1j';
  if (nb === 2) return '2j';
  if (nb === 3) return '3j';
  if (nb === 4) return '4j';
  if (nb === 5) return '5j';
  return '7j';
}

// ─── Textes officiels ─────────────────────────────────────────────────────────
export const BOOKING_POLICY = {
  tarifs:        'Nos tarifs publics restent identiques toute l\'année.',
  retour:        'Retour du matériel : le lendemain avant 10H00 maximum.',
  recuperation:  'Les récupérations sont effectuées jusqu\'à 19h maximum.',
  installation:  'Nous recommandons une livraison minimum 1 heure avant le début de votre événement.',
  anticipation:  'Toute livraison anticipée ou récupération décalée est considérée comme une demi-journée supplémentaire.',
  reductionSem:  'Les réservations du lundi au jeudi bénéficient automatiquement de -15%.',
  multiJours:    'À partir du 3ème jour : -15% sur le 3ème jour et les jours suivants.',
  horaires: {
    journee:     'Livraison entre 7h et 10h',
    demi_matin:  'Livraison entre 7h et 9h',
    demi_aprem:  'Livraison entre 13h et 14h',
  },
};
